import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, X, Upload, Camera, ExternalLink, Loader2 } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { isImageTooDark, fallbackForCampsite } from "../../lib/brightImages";

/**
 * PhotoGallery — main hero image with thumbnail strip + lightbox
 * + "Submit a photo" CTA for logged-in users.
 *
 * images shape: array of {url, source, credit, license, page_url, caption}
 *   source: 'wikimedia' | 'stock' | 'user'
 */
export default function PhotoGallery({ campsiteId, images = [], onPhotoAdded }) {
  const { user } = useAuth();
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [swappedUrl, setSwappedUrl] = useState(null);
  const fileRef = useRef(null);

  // Normalise: legacy single string urls also supported
  const items = (images || []).map((x) => (typeof x === "string" ? { url: x, source: "stock" } : x))
    .filter((x) => x && x.url);

  const current = items[active] || items[0];
  const next = () => setActive((a) => (a + 1) % items.length);
  const prev = () => setActive((a) => (a - 1 + items.length) % items.length);

  // Auto-swap dark/broken images for a curated bright fallback. Runs only
  // for the currently-displayed image (lazy), caches verdicts per URL in
  // sessionStorage so we never re-analyse. The fallback is deterministic
  // per campsite, so visitors don't see flicker between renders.
  // NOTE: this hook MUST run on every render (no early-return above it)
  // to satisfy the rules-of-hooks lint check.
  useEffect(() => {
    setSwappedUrl(null);
    if (!current?.url) return;
    let cancel = false;
    isImageTooDark(current.url).then((tooDark) => {
      if (cancel || !tooDark) return;
      setSwappedUrl(fallbackForCampsite(campsiteId || current.url));
    });
    return () => { cancel = true; };
  }, [current?.url, campsiteId]);

  if (items.length === 0) {
    return (
      <div className="aspect-[16/9] bg-gradient-to-br from-[#ff6a2c] to-[#e63a00] flex items-center justify-center">
        <Camera className="w-16 h-16 text-white/60" strokeWidth={1.25} />
      </div>
    );
  }

  const displayUrl = swappedUrl || current.url;

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Image too large (max 6 MB)");
      return;
    }
    setUploading(true);
    try {
      // Read as data URL (base64)
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post("/photos/submit", {
        campsite_id: campsiteId,
        image_data_url: dataUrl,
        caption: caption.trim().slice(0, 200),
      });
      toast.success("Thanks! A moderator will review your photo shortly 🙏");
      setUploadOpen(false);
      setCaption("");
      onPhotoAdded?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const sourceBadge = (it) => {
    if (it.source === "wikimedia") return { label: "Wikimedia", tint: "bg-sky-50 text-sky-800 border-sky-200" };
    if (it.source === "user") return { label: "Camper photo", tint: "bg-emerald-50 text-emerald-800 border-emerald-200" };
    return { label: "Stock", tint: "bg-stone-50 text-stone-700 border-stone-200" };
  };

  const badge = sourceBadge(current);

  return (
    <div data-testid="photo-gallery">
      {/* Hero image — cream container instead of near-black so any photos
          with transparency / aspect padding don't read as a void.
          NO brightness CSS filter (user feedback iter98): instead we
          auto-detect black/dark images via canvas luma sampling and swap
          for a curated bright UK-camping stock photo (see brightImages.js). */}
      <div className="relative aspect-[16/9] bg-[#f5efe6] overflow-hidden">
        <img
          src={displayUrl}
          alt={current.caption || "Campsite"}
          className="w-full h-full object-cover cursor-zoom-in"
          onClick={() => setLightboxOpen(true)}
          onError={(e) => {
            // Network failure on the original → swap to fallback immediately
            if (!swappedUrl) {
              setSwappedUrl(fallbackForCampsite(campsiteId || current.url));
            } else {
              e.currentTarget.style.display = "none";
            }
          }}
          data-testid="gallery-hero-img"
        />
        {/* Source badge */}
        <div className={`absolute top-3 left-3 px-2 py-1 text-[10px] uppercase tracking-wider font-bold border ${badge.tint}`}>
          {badge.label}
        </div>
        {/* Counter */}
        {items.length > 1 && (
          <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/70 text-white text-[11px] mono">
            {active + 1} / {items.length}
          </div>
        )}
        {/* Prev / next */}
        {items.length > 1 && (
          <>
            <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/85 hover:bg-white border hairline flex items-center justify-center" aria-label="Previous photo" data-testid="gallery-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/85 hover:bg-white border hairline flex items-center justify-center" aria-label="Next photo" data-testid="gallery-next">
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Credit line */}
      {(current.credit || current.page_url) && (
        <div className="text-[10px] text-[#888] mt-1.5 flex items-center gap-2">
          {current.credit && <span>📷 {current.credit}</span>}
          {current.page_url && (
            <a href={current.page_url} target="_blank" rel="noopener noreferrer" className="text-[#0369a1] hover:underline inline-flex items-center gap-0.5">
              source <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
      )}

      {/* Thumbnail strip + upload */}
      <div className="mt-3 flex gap-2 items-center overflow-x-auto pb-1">
        {items.map((it, i) => (
          <button key={i} onClick={() => setActive(i)} className={`relative w-16 h-16 shrink-0 overflow-hidden border-2 ${i === active ? "border-[#ff4500]" : "border-transparent"}`} aria-label={`View photo ${i+1}`} data-testid={`gallery-thumb-${i}`}>
            <img src={it.url} alt="" className="w-full h-full object-cover" loading="lazy"
              onError={(e) => { e.currentTarget.style.background = "#fde9e0"; e.currentTarget.style.display = "block"; }} />
          </button>
        ))}
        {user && (
          <button onClick={() => setUploadOpen(true)} className="w-16 h-16 shrink-0 border-2 border-dashed border-[#ff4500] text-[#ff4500] hover:bg-[#fff4ee] flex flex-col items-center justify-center" data-testid="gallery-add-btn">
            <Upload className="w-4 h-4" strokeWidth={2} />
            <span className="text-[8px] uppercase tracking-wider mt-0.5">Add</span>
          </button>
        )}
      </div>

      {/* Upload modal */}
      {uploadOpen && (
        <div className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4" data-testid="upload-modal">
          <div className="bg-white max-w-md w-full p-6 border hairline-strong">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="eyebrow text-[#ff4500]">Submit a photo</div>
                <h3 className="font-display text-xl mt-1">Share a real shot of this campsite</h3>
              </div>
              <button onClick={() => setUploadOpen(false)} aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#555] mb-4 leading-relaxed">
              Help fellow campers see what this site really looks like. By uploading you confirm you took the photo or have permission to share it. A moderator will review before it goes live.
            </p>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Optional caption — e.g. 'Pitch 12, October 2025'"
              className="w-full p-3 border hairline text-sm mb-3 resize-none"
              rows={2}
              maxLength={200}
              data-testid="upload-caption"
            />
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" onChange={handleFileSelect} className="hidden" data-testid="upload-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full py-3 bg-[#ff4500] text-white uppercase tracking-wider text-xs font-bold hover:bg-[#e03d00] disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-[0_4px_0_0_#111]"
              data-testid="upload-submit"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              {uploading ? "Uploading…" : "Choose photo"}
            </button>
            <p className="text-[10px] text-[#888] mt-2 text-center">
              JPG / PNG / WebP · max 6 MB · stripped of location metadata before storing
            </p>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <div onClick={() => setLightboxOpen(false)} className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out" data-testid="lightbox">
          <img src={current.url} alt="" className="max-w-full max-h-[90vh] object-contain" />
          <button onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }} className="absolute top-4 right-4 text-white/80 hover:text-white" aria-label="Close">
            <X className="w-8 h-8" />
          </button>
          {items.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white" aria-label="Previous">
                <ChevronLeft className="w-10 h-10" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white" aria-label="Next">
                <ChevronRight className="w-10 h-10" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
